import React from "react";

function article_1() {
	return {
		date: "10 Feb 2025",
		title: "Most Developers Think They Understand APIs — Until They Build a Real One",
		description:
			"APIs are more than request/response code. They are contracts, security layers, performance boundaries, and business logic systems. Here’s what separates demo APIs from production-grade APIs.",
		keywords: [
			"API design",
			"REST",
			"Backend engineering",
			"MERN stack",
			"Node.js",
			"Express",
			"Full-stack development",
			"Aryan Vashisht",
		],
		body: (
			<>
				<p>
					A lot of developers assume they “know APIs” because they can return JSON
					from a route. That’s not API engineering — that’s barely scratching the surface.
				</p>

				<h3>What an API actually represents</h3>
				<ul>
					<li>A contract between systems & clients</li>
					<li>A security and validation checkpoint</li>
					<li>A performance bottleneck if designed wrong</li>
					<li>Business logic orchestration layer</li>
					<li>Scalable architecture entry point</li>
				</ul>

				<p>
					A real API handles load, failure, expired tokens, malformed input, abuse,
					and real-world chaos — not just perfect requests from Postman.
				</p>

				<h3>Common API pitfalls</h3>
				<ul>
					<li>Business logic inside route handlers</li>
					<li>No request validation (Zod/Joi ignored)</li>
					<li>Single-file backend structure</li>
					<li>Weak or confusing status codes</li>
					<li>No centralized error middleware</li>
					<li>No rate-limiting or security guardrails</li>
				</ul>

				<h3>Building APIs the right way</h3>
				<ul>
					<li>Understand HTTP deeply — not superficially</li>
					<li>Use controllers + service layers</li>
					<li>Implement proper JWT + refresh strategy</li>
					<li>Use DB query layers (Prisma/ORM/Mongoose models)</li>
					<li>Add observability — logs, metrics, monitoring</li>
					<li>Handle edge cases first, not last</li>
				</ul>

				<p>
					APIs aren't about frameworks — they’re about structure, reliability, and security.
					The moment your app gets real traffic, shortcuts start showing.
				</p>

				<p>
					Anyone can build an endpoint.  
					Few can build a system that scales and survives failure.
				</p>

				<i>
					Build APIs for problems you haven't seen yet — that's real backend engineering.
				</i>
			</>
		),
	};
}

function article_2() {
	return {
		date: "12 Jul 2025",
		title: "Most Developers Get JWT Refresh Tokens Wrong — Here’s the Correct Way",
		description:
			"Access tokens expire fast. Refresh tokens live longer. Sounds simple, yet most developers implement it like a tutorial and end up with insecure authentication. This is the real architecture.",
		keywords: [
			"JWT",
			"Refresh Token",
			"Access Token",
			"Authentication",
			"Node.js Security",
			"MERN Auth",
			"Secure Token Flow",
			"Aryan Vashisht",
		],
		body: (
			<>
				<p>
					Here's the truth: 90% of developers copy-paste JWT logic from a
					blog, store refresh tokens in localStorage (🤦‍♂️), and then
					wonder why their auth system is vulnerable.
				</p>

				<p><b>Access Token</b> = short-lived (typically 5–15 mins)</p>
				<p><b>Refresh Token</b> = long-lived + highly sensitive</p>

				<p>
					If your refresh token gets stolen, the attacker becomes the
					user. Period. That means storing it in localStorage or
					sessionStorage is not “okay for now” — it’s bad engineering.
				</p>

				<h3> The Correct Architecture</h3>
				<ul>
					<li>Access token — sent via Authorization header</li>
					<li>Refresh token — stored in HttpOnly, Secure Cookie</li>
					<li>Refresh token rotation — ALWAYS</li>
					<li>Invalidate tokens server-side (DB or Redis)</li>
					<li>Logout should blacklist/expire the refresh token</li>
				</ul>

				<p><b>Flow:</b></p>
				<ol>
					<li>User logs in → server issues access + refresh tokens</li>
					<li>Frontend stores only access token in memory</li>
					<li>Refresh token stays in HttpOnly cookie</li>
					<li>When access token expires → hit /refresh endpoint</li>
					<li>Server validates + rotates refresh token → issues new pair</li>
				</ol>

				<h3> What not to do</h3>
				<ul>
					<li>Store refresh token in localStorage</li>
					<li>Use same refresh token forever</li>
					<li>Skip server-side token invalidation</li>
					<li>Return 200 on expired tokens (no.)</li>
				</ul>

				<p>
					Every serious application (banking, e-commerce, SaaS) uses this
					pattern. If you don't understand it, you don't understand
					authentication — you're just passing JWT strings around.
				</p>

				<p>
					<b>Authentication isn’t about logging in — it's about defending identities.</b>
				</p>

				<i>Security isn't an optional feature — it's the baseline.</i>
			</>
		),
	};
}

const myArticles = [article_1, article_2];

export default myArticles;
